import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Team,
  TeamInvite,
  TeamInviteStatus,
  TeamRole,
} from '@this+that/models';
import log from 'loglevel';

import { useTeamActions } from './useTeamActions';
import { useTeamUndoAction } from './useTeamUndoAction';
import { useCustomToast } from '../../../../hooks/useCustomToast';
import {
  useGetPaginatedTeamMembers,
  useGetTeamById,
  useGetUserTeamIds,
} from '../../../../services/team.service';
import {
  useAcceptInvite,
  useGetUserTeamInvites,
  useRejectInvite,
} from '../../../../services/teamInvite.service';
import {
  isTeamCacheStale,
  useTeamStore,
} from '../../../../store/Team/useTeamStore';
import { TEAM_FETCH_LIMIT } from '../constants';
import { bucketTeamHubCards } from '../utils/bucketTeamHubCards';

const ABORT_ERROR_NAME = 'AbortError';
const ABORT_ERROR_MARKERS = ['abort', 'the user aborted', 'signal is aborted'];

const LOAD_TEAMS_MAX_RETRIES = 2;
const LOAD_TEAMS_RETRY_DELAY_MS = 800;

const ACCEPT_INVITE_TEAM_FETCH_MAX_RETRIES = 3;
const ACCEPT_INVITE_TEAM_FETCH_RETRY_DELAY_MS = 700;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Aborted requests from page reload/navigation must not surface as a load failure.
function isAbortError(error: unknown): boolean {
  if (!(error instanceof Object)) {
    return false;
  }

  const err = error as Record<string, unknown>;

  if (typeof err['name'] === 'string' && err['name'] === ABORT_ERROR_NAME) {
    return true;
  }

  const networkError = err['networkError'];
  if (
    networkError instanceof Object &&
    (networkError as Record<string, unknown>)['name'] === ABORT_ERROR_NAME
  ) {
    return true;
  }

  const cause = err['cause'];
  if (
    cause instanceof Object &&
    (cause as Record<string, unknown>)['name'] === ABORT_ERROR_NAME
  ) {
    return true;
  }

  if (typeof err['message'] === 'string') {
    const lower = err['message'].toLowerCase();
    if (ABORT_ERROR_MARKERS.some((marker) => lower.includes(marker))) {
      return true;
    }
  }

  return false;
}

export interface TeamHubCard {
  id: string;
  name: string;
  hexColor?: string;
  myRole?: TeamRole;
  isOwnedByMe?: boolean;
  ownerName?: string;
  members: { name?: string }[];
  memberCount: number;
  consultingTeam?: boolean;
  trialHandedOffAt?: number;
}

function buildTeamHubCard(
  teamData: {
    id?: string;
    name: string;
    hexColor?: string;
    userRole?: TeamRole;
    consultingTeam?: boolean;
    trialHandedOffAt?: number;
  },
  members: { userRole?: TeamRole; userName?: string }[],
): TeamHubCard {
  const ownerRow = members.find((m) => m.userRole === TeamRole.OWNER);
  return {
    id: teamData.id ?? '',
    name: teamData.name,
    hexColor: teamData.hexColor,
    myRole: teamData.userRole,
    isOwnedByMe: teamData.userRole === TeamRole.OWNER,
    ownerName: ownerRow?.userName,
    members: members.map((m) => ({ name: m.userName })),
    memberCount: members.length,
    consultingTeam: teamData.consultingTeam,
    trialHandedOffAt: teamData.trialHandedOffAt,
  };
}

type GetTeamByIdFn = ReturnType<typeof useGetTeamById>['getTeamById'];
type GetPaginatedTeamMembersFn = ReturnType<
  typeof useGetPaginatedTeamMembers
>['getPaginatedTeamMembers'];

async function fetchAcceptedTeamWithRetry(
  teamId: string,
  getTeamById: GetTeamByIdFn,
  getPaginatedTeamMembers: GetPaginatedTeamMembersFn,
): Promise<{ team: Team; members: Team[] } | null> {
  for (
    let attempt = 0;
    attempt < ACCEPT_INVITE_TEAM_FETCH_MAX_RETRIES;
    attempt++
  ) {
    try {
      const team = await getTeamById(teamId);
      if (team) {
        const membersPage = await getPaginatedTeamMembers(
          teamId,
          TEAM_FETCH_LIMIT,
        );
        return { team, members: membersPage?.members ?? [] };
      }
    } catch (err) {
      log.error('useTeamHub.fetchAcceptedTeamWithRetry', teamId, err);
    }
    if (attempt < ACCEPT_INVITE_TEAM_FETCH_MAX_RETRIES - 1) {
      await sleep(ACCEPT_INVITE_TEAM_FETCH_RETRY_DELAY_MS);
    }
  }
  return null;
}

export function useTeamHub({ autoLoad = true }: { autoLoad?: boolean } = {}) {
  const { t } = useTranslation();
  const { errorMessage, successMessage } = useCustomToast();

  const [loading, setLoading] = useState(
    () => !useTeamStore.getState().userTeamIds,
  );
  const [searchQuery, setSearchQuery] = useState('');

  const userTeamIds = useTeamStore((s) => s.userTeamIds);
  const teamsById = useTeamStore((s) => s.teamsById);
  const pendingInvitesCache = useTeamStore((s) => s.pendingInvites);
  const acceptingInviteIds = useTeamStore((s) => s.acceptingInviteIds);

  const { getUserTeamIds } = useGetUserTeamIds();
  const { getTeamById } = useGetTeamById();
  const { getPaginatedTeamMembers } = useGetPaginatedTeamMembers();
  const { getUserTeamInvites } = useGetUserTeamInvites();
  const { acceptInvite: acceptInviteMutation } = useAcceptInvite();
  const { rejectInvite } = useRejectInvite();
  const {
    createTeam,
    createConsultingTeam,
    createLoading,
    createConsultingTeamLoading,
  } = useTeamActions();
  const { schedule } = useTeamUndoAction();

  const teams = useMemo(() => {
    if (!userTeamIds) {
      return [];
    }
    return userTeamIds
      .map((id) => teamsById[id])
      .filter((cached): cached is NonNullable<typeof cached> => !!cached)
      .map((cached) => buildTeamHubCard(cached.team, cached.members));
  }, [userTeamIds, teamsById]);

  const teamBuckets = useMemo(() => bucketTeamHubCards(teams), [teams]);

  const pendingInvites = pendingInvitesCache?.items ?? [];

  const loadTeams = useCallback(
    async (force = false) => {
      const attemptLoad = async () => {
        let ids = useTeamStore.getState().userTeamIds;
        const idsStale = isTeamCacheStale(
          useTeamStore.getState().userTeamIdsLoadedAt,
        );
        if (force || !ids || idsStale) {
          ids = (await getUserTeamIds()) ?? [];
          useTeamStore.getState().setUserTeamIds(ids);
        }

        if (!ids.length) {
          return;
        }

        const cached = useTeamStore.getState().teamsById;
        const toFetch = force
          ? ids
          : ids.filter((id) => isTeamCacheStale(cached[id]?.loadedAt));

        if (toFetch.length === 0) {
          return;
        }

        setLoading(true);
        const results = await Promise.allSettled(
          toFetch.map((id) =>
            Promise.all([
              getTeamById(id),
              getPaginatedTeamMembers(id, TEAM_FETCH_LIMIT),
            ]),
          ),
        );

        let hasLoadedAnyTeam = false;
        let hasRealError = false;
        results.forEach((result, index) => {
          if (result.status !== 'fulfilled') {
            if (isAbortError(result.reason)) {
              log.debug('useTeamHub.loadTeams aborted', toFetch[index]);
            } else {
              log.error('useTeamHub.loadTeams', toFetch[index], result.reason);
              hasRealError = true;
            }
            return;
          }

          const [teamData, membersData] = result.value;
          if (!teamData) {
            return;
          }

          useTeamStore
            .getState()
            .setTeamData(toFetch[index], teamData, membersData?.members ?? []);
          hasLoadedAnyTeam = true;
        });

        if (!hasLoadedAnyTeam && hasRealError) {
          errorMessage(t('teamHub.toasts.errorLoad'));
        }
      };

      let attempt = 0;
      try {
        while (attempt <= LOAD_TEAMS_MAX_RETRIES) {
          try {
            await attemptLoad();
            return;
          } catch (err) {
            if (isAbortError(err)) {
              if (attempt < LOAD_TEAMS_MAX_RETRIES) {
                log.debug('useTeamHub.loadTeams abort — retrying', attempt + 1);
                attempt++;
                await sleep(LOAD_TEAMS_RETRY_DELAY_MS);
                continue;
              }
              log.debug('useTeamHub.loadTeams abort — max retries reached');
              return;
            }
            log.error('useTeamHub.loadTeams', err);
            errorMessage(t('teamHub.toasts.errorLoad'));
            return;
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [getUserTeamIds, getTeamById, getPaginatedTeamMembers, errorMessage, t],
  );

  const loadPendingInvites = useCallback(
    async (force = false) => {
      const cache = useTeamStore.getState().pendingInvites;
      if (!force && cache && !isTeamCacheStale(cache.loadedAt)) {
        return;
      }
      try {
        const invites = await getUserTeamInvites(
          TeamInviteStatus.PENDING,
          true,
        );
        useTeamStore.getState().setPendingInvites(invites ?? []);
      } catch (err) {
        if (isAbortError(err)) {
          log.debug('useTeamHub.loadPendingInvites aborted', err);
          return;
        }
        log.error('useTeamHub.loadPendingInvites', err);
      }
    },
    [getUserTeamInvites],
  );

  useEffect(() => {
    if (!autoLoad) {
      return;
    }
    // mount-only: the loaders read current state via the store (getState),
    // so they don't belong in deps — listing them would refetch on every store change
    loadTeams();
    loadPendingInvites();
  }, []);

  const acceptInvite = async (invite: TeamInvite) => {
    const store = useTeamStore.getState();
    if (store.acceptingInviteIds[invite.teamId]) {
      return;
    }
    store.beginAcceptingInvite(invite.teamId);
    try {
      const accepted = await acceptInviteMutation(invite.teamId);
      if (!accepted) {
        errorMessage(t('teamHub.undo.error'));
        return;
      }

      useTeamStore.getState().addUserTeamId(invite.teamId);
      useTeamStore.getState().removePendingInvite(invite.teamId);
      successMessage(t('teamHub.undo.accepted', { team: invite.teamName }));

      const fetched = await fetchAcceptedTeamWithRetry(
        invite.teamId,
        getTeamById,
        getPaginatedTeamMembers,
      );
      if (fetched) {
        useTeamStore
          .getState()
          .setTeamData(invite.teamId, fetched.team, fetched.members);
      }
    } catch (err) {
      log.error('useTeamHub.acceptInvite', err);
      errorMessage(t('teamHub.undo.error'));
    } finally {
      useTeamStore.getState().endAcceptingInvite(invite.teamId);
    }
  };

  const declineInvite = (invite: TeamInvite) => {
    useTeamStore.getState().removePendingInvite(invite.teamId);
    schedule({
      key: `invite-${invite.teamId}`,
      message: t('teamHub.undo.declined', { team: invite.teamName }),
      commit: async () => {
        await rejectInvite(invite.teamId);
      },
      revert: () => useTeamStore.getState().prependPendingInvite(invite),
    });
  };

  const filteredTeams = searchQuery
    ? teams.filter((team) =>
        team.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : teams;

  const filteredTeamBuckets = useMemo(
    () => bucketTeamHubCards(filteredTeams),
    [filteredTeams],
  );

  return {
    loading,
    teams,
    teamBuckets,
    filteredTeams,
    filteredTeamBuckets,
    searchQuery,
    setSearchQuery,
    pendingInvites,
    acceptInvite,
    declineInvite,
    acceptingInviteIds,
    createTeam,
    createConsultingTeam,
    createLoading,
    createConsultingLoading: createConsultingTeamLoading,
    ensureTeamsLoaded: () => loadTeams(),
    ensurePendingInvitesLoaded: () => loadPendingInvites(),
    reloadTeams: () => loadTeams(true),
  };
}
